import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout, FieldGrid,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Operations → New Business → New Business (New Client) / New Account (Existing Client).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Is this for a New or Existing
 * Client?", "New Business (New Client)" / "New Business (Existing Client)",
 * and "Attach New Business (New Client) Files" / "Attach New Business
 * (Existing Client) Files". Both sub-categories share the confirmation screen,
 * the Account Information questions, the fee split table and the submit
 * screen; they differ in the case header and the client questions, which are
 * split by screen heading below. The Existing Client question order follows
 * the New Client screen it mirrors. Shared chrome and policy copy live in
 * GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'New Business',
    subCategory: 'New Business (New Client) for someone we have no record of, or New Account (Existing Client) for someone already on our books',
    subject: subjectRow('New account - IRA Rollover at Schwab', { noAccount: true }),
    lookups: [
      ['Household', 'New Account (Existing Client) only. The client household the new account joins. The advisor and office on the case are taken from it.'],
      ['Advisor', 'New Business (New Client) only. The advisor bringing the client on. The office on the case is taken from the advisor.'],
    ],
    note: (
      <>
        Only one of the two pickers shows - a new client has no household yet, so you name the advisor instead.
        The account does not have a number yet, so describe the new account in the Subject rather than using a
        number, and never the client&rsquo;s name.
      </>
    ),
  }),
  {
    title: 'Confirm New or Existing Client',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The next screen, <strong>Are you navigating to the right area?</strong>, explains the difference with
          two examples and asks one required question, <em>Is this for a New or Existing Client?</em>
        </p>
        <FieldGrid rows={[
          ['New Business → NEW Client', 'The person is nowhere in our system or books and records. Their paperwork creates both the client and their first account(s).'],
          ['New Business → Existing Client', 'The person already exists on our books - even with one small account, from years ago, or with a different advisor - and you are opening an additional account, bringing over additional assets, or both.'],
        ]} />
        <p style={{ margin: 0 }}>
          When in doubt, search for them in FSC first: found means Existing, not found means New. <strong>This
          answer decides which questions you get</strong>, so make it match the Sub-Category you chose.
        </p>
      </>
    ),
  },
  {
    title: 'Answer the client questions',
    body: (
      <p style={{ margin: 0 }}>
        The details screen asks for the client&rsquo;s personal details first. Questions reveal themselves one at
        a time as you answer, so you only see what applies. It ends with a required <strong>Additional
        Instructions</strong> box - this becomes the case description Operations reads first, so put anything
        that does not fit the questions here: funding source, transfers or rollovers coming in, timing, and
        anything unusual about the registration.
      </p>
    ),
  },
  {
    title: 'Complete Account Information',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Once Additional Instructions has text, an <strong>Account Information</strong> section opens below it
          with the custodian, paperwork, SMA, Orion Custom Indexing and fee split questions.
        </p>
        <p style={{ margin: 0 }}>
          If you answer that the fee will be split, an <strong>Advisors</strong> table appears. Tick every advisor
          involved in the split (the table has a search bar and allows several rows), then type each
          one&rsquo;s percentage into the <strong>Fee Split</strong> column - the only column you can edit. The
          other columns are Full Name, Office/Team, Email and Rep Number. If you already have a completed
          Prosperity Advisory Split Form, the screen says you can skip the table and upload the form on the next
          screen instead.
        </p>
      </>
    ),
  },
  {
    title: 'Attach the documents, acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Attach New Business (New Client) Files</strong> or <strong>Attach New Business (Existing
        Client) Files</strong> screen has upload tiles - add the account paperwork and any forms the questions
        mentioned. The screen warns that missing or incomplete documents may result in a NIGO. Read the Operations
        and Trading notice below the tiles, select <em>I have read, understand, and agree to the above
        information</em> under Acknowledgement and click Next. FSC creates the case and opens it. Note the case
        number for follow-up.
      </p>
    ),
  },
];

// Shared tail of both details screens: the Account Information questions
// from the SMA question onward. Option labels differ per screen, so they are
// passed in.
const accountInfoTail = ({ smaYes, smaNo, ociYes, ociNo }) => [
  {
    q: 'Do you have all of the required Prosperity Paperwork?',
    when: 'Once the previous question is answered',
    options: ['Yes (I have all of the required Prosperity Paperwork)', 'No (I do not have all of the required Prosperity Paperwork)'],
  },
  {
    q: 'What are you missing, and why? (And when do you expect it?)',
    when: 'Only if you chose No (I do not have all of the required Prosperity Paperwork)',
    note: 'Name each missing document, the reason, and a date you expect it.',
  },
  {
    q: 'Will the onboarding involve a Separately Managed Account (SMA)?',
    when: 'Once the paperwork question is answered (and explained, if anything is missing)',
    options: [smaYes, smaNo],
    note: `Answering ${smaNo} skips the SMA and Orion questions and goes straight to the fee split question.`,
  },
  {
    q: 'Please select the SMA Manager',
    when: `Only if you chose ${smaYes}`,
    note: 'A dropdown of the SMA managers set up in FSC.',
  },
  {
    q: 'Do you have the proposal generated for you from Investment Support?',
    when: 'Once an SMA Manager is selected',
    options: ['Yes (I have the SMA Proposal)', 'No (I do not have the SMA Proposal)'],
    note: 'The screen notes: "The Investment Proposal is required to open an SMA Case. If needing a copy, please reach out to the SMA Team at sma-team@prosperitycapitaladvisors.com". Upload the proposal on the files screen.',
    warn: true,
  },
  {
    q: 'Will this involve an ORION Custom Indexing (OCI) Strategy?',
    when: 'Once the SMA proposal question is answered',
    options: [ociYes, ociNo],
  },
  {
    q: 'Do you have the Cost Basis Update Form (Orion requires this)?',
    when: `Only if you chose ${ociYes}`,
    options: ['Yes (I have the Cost Basis Update form)', 'No (I do not have the Cost Basis Update Form)'],
    note: 'The screen links to the "Schwab Cost Basis Enrollment & Preferences Form (Not needed for Fidelity)". Upload it on the files screen.',
  },
  {
    q: 'Is there more than one Advisor servicing this Household? (Will there be a Fee Split?)',
    when: 'Once the SMA question is answered No, or the Orion questions are answered',
    options: ['Yes  (The Fee Will Be Split Between More than One Advisor)', 'No  (Only One Advisor will be servicing this Household)'],
  },
  {
    q: 'Advisors (table)',
    when: 'Only if the fee will be split',
    note: 'Tick every advisor involved in the split and enter each percentage in the Fee Split column. Or skip the table and upload a completed Prosperity Advisory Split Form on the next screen.',
  },
];

const NEW_CLIENT = [
  {
    screen: 'New Business (New Client) Details',
    q: 'Household AUM (Estimated)',
    when: 'Always - this is the first question',
    note: 'Over $1,000, the screen shows an Estimated Gemstone Tier: Opal under $1M, Ruby from $1M, Diamond from $2M, Blue Diamond from $5M, Red Diamond from $10M.',
  },
  { q: 'Client First Name · Client Last Name · Client DOB', when: 'Always' },
  {
    q: 'Client SSN',
    when: 'Always',
    note: 'Exactly 9 digits with no dashes or spaces, or the form stops you with "Enter the SSN as exactly 9 digits with no dashes or spaces."',
  },
  { q: 'Phone · Email', when: 'Once the SSN is entered', note: 'Phone in the (###) ###-#### format.' },
  { q: 'Address', when: 'Once both phone and email are entered', note: 'Start typing to search for the address, or enter it by hand. Country defaults to United States.' },
  {
    q: 'Employment Status',
    when: 'Once the address has a zip code',
    options: ['Employed', 'Retired', 'Business Owner', 'Other'],
    note: 'Not marked required, but the next question does not appear until you answer it.',
  },
  { q: 'Employment Status Information', when: 'Only if you chose Other' },
  {
    q: 'Relationship Status',
    when: 'Once Employment Status is answered',
    options: ['Single', 'Married', 'Divorced', 'Domestic Partnership', 'Not Applicable'],
  },
  { q: 'Joint First Name · Joint Last Name', when: 'Only if you chose Married or Domestic Partnership' },
  {
    q: 'Additional Instructions',
    when: 'Once Relationship Status is Single, Divorced or Not Applicable, or a joint name is entered',
    note: 'Becomes the case description. The Account Information section stays hidden until this has text.',
  },
  {
    screen: 'New Business (New Client) - Account Information',
    q: 'What Custodian will this be with?',
    when: 'Once Additional Instructions has text',
    options: ['Schwab', 'Fidelity'],
  },
  {
    q: 'Has there been paperwork submitted at the Custodian?',
    when: 'Once a custodian is selected',
    options: ['Yes (Paperwork has been submitted to the Custodian)', 'No (Paperwork has not been submitted to the Custodian)'],
  },
  {
    q: 'What has been submitted (or what needs to be)?',
    when: 'Once the custodian paperwork question is answered',
    note: 'List the custodian forms already in, or the ones still to go.',
  },
  ...accountInfoTail({
    smaYes: 'Yes (This will involve an SMA)',
    smaNo: 'No (This will not involve an SMA)',
    ociYes: 'Yes (This will involve an Orion Custom Indexing account)',
    ociNo: 'No (This will NOT involve an Orion Custom Indexing account)',
  }),
];

const EXISTING_CLIENT = [
  {
    screen: 'New Accounts (Existing Client) Details',
    q: 'Client First Name · Client Last Name · Client DOB',
    when: 'Always',
    note: 'The owner of the new account.',
  },
  {
    q: 'Account Type',
    when: 'Always',
    options: ['IRA', 'TSA', 'HAS', '401(K)', '403(B)', 'SIMPLE', 'SEP IRA', '529 Plan', 'JTWROS', 'Business', 'Roth IRA', 'Non-Profit', 'Individual', 'Minor Roth', 'UGMA / UTMA', 'IRA Rollover', 'Individual TOD', 'Trust (Single)', 'Beneficiary IRA', 'Trust (Multiple)', 'Roth Rollover IRA', 'Beneficiary Roth IRA'],
  },
  { q: 'Custodian', when: 'Always', options: ['Schwab', 'Fidelity'] },
  { q: 'Phone · Email', when: 'Always', note: 'Phone in the (###) ###-#### format.' },
  {
    q: 'Client SSN',
    when: 'Always',
    note: 'Exactly 9 digits with no dashes or spaces, or the form stops you with "Enter the SSN as exactly 9 digits with no dashes or spaces."',
  },
  { q: 'Address', when: 'Once both SSN and email are entered', note: 'Start typing to search for the address, or enter it by hand. Country defaults to United States.' },
  {
    q: 'Additional Instructions',
    when: 'Once the address has a state or zip code',
    note: 'Becomes the case description. The Account Information section stays hidden until this has text.',
  },
  {
    screen: 'New Accounts (Existing Client) - Account Information',
    q: 'Estimated Account Size (AUM)?',
    when: 'Once Additional Instructions has text',
  },
  {
    q: 'Has there been paperwork submitted at the Custodian?',
    when: 'Only once the estimated account size is more than $100',
    options: ['Yes (Paperwork has been submitted to the Custodian)', 'No (Paperwork has not been submitted to the Custodian)'],
  },
  {
    q: 'What has been submitted (or what needs to be)?',
    when: 'Once the custodian paperwork question is answered',
    note: 'List the custodian forms already in, or the ones still to go.',
  },
  {
    q: 'Will this involve another account owner?',
    when: 'Once you have described what has been submitted',
    options: ['Yes (This will involve another account owner)', 'No (This will NOT involve another account owner)'],
  },
  { q: 'Joint First Name · Joint Last Name', when: 'Only if you chose Yes (This will involve another account owner)' },
  ...accountInfoTail({
    smaYes: 'Yes (This is for an SMA)',
    smaNo: 'No (This is not for an SMA)',
    ociYes: 'Yes (This is for Orion Custom Indexing)',
    ociNo: 'No (This is not for Orion Custom Indexing)',
  }),
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'New Business'),
  {
    h: "There's no Household picker on the first screen",
    p: [
      'That is expected for New Business (New Client) - the client has no household yet, so the screen asks for the Advisor instead. If the client is already on our books, change the Sub-Category to New Account (Existing Client) and the Household picker appears.',
    ],
  },
  {
    h: "I'm getting the wrong set of questions",
    p: [
      'The details screen follows your answer to "Is this for a New or Existing Client?", not the Sub-Category. Click Previous and change the answer so it matches the Sub-Category. If the Sub-Category itself is wrong, cancel and start again.',
    ],
  },
  {
    h: "The Account Information questions haven't appeared",
    p: ['They stay hidden until the Additional Instructions box has something in it.'],
  },
  {
    h: "A question I expected hasn't shown up",
    p: [
      'Most questions wait for the one before. Phone and email (New Client) wait for the SSN; the address waits for phone and email (New Client) or SSN and email (Existing Client); the next question waits for the address zip code. On the Existing Client screen, the custodian paperwork question only appears once the estimated account size is more than $100.',
    ],
  },
  {
    h: 'An advisor in the fee split is not in the Advisors table',
    p: [
      'Use the search bar on the table first. If they are still missing, upload a completed Prosperity Advisory Split Form on the files screen instead of using the table.',
    ],
  },
  {
    h: 'I picked the wrong household or advisor',
    p: [
      'The household (Existing Client) or advisor (New Client) chosen on the first screen decides the advisor and office stamped on the case. If either is wrong, cancel and start again rather than working around it.',
    ],
  },
];

export default function NewBusiness() {
  return (
    <GuidePage
      title="New Business Requests"
      icon="person-plus"
      path="Operations → New Business → New Business (New Client) or New Account (Existing Client)"
      minutes={8}
      intro="How to submit new business to the Prosperity Operations team - a brand-new client, or a new account or new assets for a client already on our books. What to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>
          First answer one question: <strong>does this person already exist on our books?</strong> If yes - even
          with one small account, even from years ago, even with a different advisor - it is New Account (Existing
          Client). If we have no record of them anywhere, it is New Business (New Client). Search FSC if you are not
          sure.
        </Para>
        <Para>Then have these ready:</Para>
        <Bullets items={[
          <><strong>The client&rsquo;s details</strong> - first and last name, date of birth, SSN (9 digits), phone, email and address. For a new client, also employment status and relationship status, with the spouse or partner&rsquo;s name if married or in a domestic partnership.</>,
          <><strong>For an existing client</strong> - the household, the type of account being opened, and the estimated account size; plus the joint owner&rsquo;s name if there is another account owner.</>,
          <><strong>For a new client</strong> - the advisor bringing them on, and an estimate of the household&rsquo;s total AUM.</>,
          <><strong>The custodian</strong> - Schwab or Fidelity - and what paperwork has been, or still needs to be, submitted there.</>,
          <><strong>The Prosperity paperwork</strong>, or a list of what is missing, why, and when you expect it.</>,
          <><strong>For an SMA</strong> - the SMA manager and the proposal from Investment Support. For Orion Custom Indexing at Schwab, the Schwab Cost Basis Enrollment &amp; Preferences Form.</>,
          <><strong>For a fee split</strong> - every advisor involved and their percentage, or a completed Prosperity Advisory Split Form.</>,
          <><strong>The documents themselves</strong>, ready to upload on the last screen.</>,
        ]} />
        <Callout tone="warn">
          The screen warns that missing or incomplete documents may result in a NIGO, and that the Investment
          Proposal is required to open an SMA case.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>
          Use this to prepare before you sit down with a client, or to work out why a question you expected has not
          shown up. Every question is required once it appears unless noted. Find the screen that matches your
          answer to &ldquo;Is this for a New or Existing Client?&rdquo;.
        </Para>
        <QuestionList questions={NEW_CLIENT} />
        <div style={{ marginTop: '1.5rem' }}>
          <QuestionList questions={EXISTING_CLIENT} />
        </div>
      </Section>

      <WhatHappensNext
        team="operations"
        before={(
          <Para>
            The client&rsquo;s name, date of birth, SSN, phone, email, joint owner name, AUM estimate, custodian and
            any fee split are recorded on the case alongside your answers. Additional Instructions becomes the case
            description.
          </Para>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe account opening requirements at the
        custodian or the suitability review.
      </Footnote>
    </GuidePage>
  );
}
